import { HeartIcon, KeyIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import { QueryClient } from '@tanstack/react-query';
import { Cloud as CloudIcon, CloudOff } from 'lucide-react';

import type { Agent } from '@/hooks/useAgents';
import type { AgentDrawerTabId } from '@/sections/agents/components/drawer/types';

/**
 * Modal types that can be opened directly from row actions.
 * Following IA best practices: direct modal access without requiring drawer navigation.
 */
export type AgentModalType =
  | 'addUser'
  | 'addCredential'
  | 'addTunnel'
  | 'removeTunnel';

/**
 * Generate row actions for agents table.
 *
 * Actions can either:
 * - Open the drawer on specific tabs (View Agent Health, Add/Remove Tunnel)
 * - Open modals directly (Add User, Add Credentials)
 *
 * Following IA principles: direct access to modals improves discoverability and UX.
 */
export const getAgentRowActions = (
  agent: Agent,
  isPraetorianUser: boolean,
  queryClient: QueryClient,
  onOpenDrawerWithTab: (agent: Agent, tab: AgentDrawerTabId) => void,
  onOpenModal?: (modal: AgentModalType, agent: Agent) => void
) => {
  // Get enhanced data from cache for tunnel status detection
  const enhancedAgent =
    queryClient.getQueryData<Agent>(['agent', agent.client_id, 'enhanced']) ??
    agent;

  const hasTunnel =
    enhancedAgent.health_check?.cloudflared_status?.status === 'running';

  const menuItems = [
    {
      label: 'View Endpoint Health',
      icon: <HeartIcon className="size-4" />,
      onClick: () => onOpenDrawerWithTab(agent, 'health'),
    },
    {
      label: 'Add Credentials',
      icon: <KeyIcon className="size-4" />,
      // Direct modal access when callback provided, fallback to drawer tab
      onClick: () =>
        onOpenModal
          ? onOpenModal('addCredential', agent)
          : onOpenDrawerWithTab(agent, 'credentials'),
    },
    // Praetorian-only actions
    ...(isPraetorianUser
      ? [
          {
            label: 'Add User',
            icon: <UserPlusIcon className="size-4" />,
            // Direct modal access when callback provided, fallback to drawer tab
            onClick: () =>
              onOpenModal
                ? onOpenModal('addUser', agent)
                : onOpenDrawerWithTab(agent, 'users'),
          },
          {
            label: hasTunnel ? 'Remove Tunnel' : 'Add Tunnel',
            icon: hasTunnel ? (
              <CloudOff className="size-4" />
            ) : (
              <CloudIcon className="size-4" />
            ),
            // Direct modal access when callback provided, fallback to drawer tab
            onClick: () =>
              onOpenModal
                ? onOpenModal(hasTunnel ? 'removeTunnel' : 'addTunnel', agent)
                : onOpenDrawerWithTab(agent, 'tunnel'),
          },
        ]
      : []),
  ];

  return {
    menu: { items: menuItems },
  };
};
